import { onUnmounted, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import tourService from '@/service/tour/tour.service.js';
import dashboardTourConfig from '@/config/tour/dashboard.tour.js';
import songLiveTourConfig from '@/config/tour/songLive.tour.js';
import songEditorTourConfig from '@/config/tour/songEditor.tour.js';
import scheduleTourConfig from '@/config/tour/schedule.tour.js';
import teamListTourConfig from '@/config/tour/teamList.tour.js';
import teamFormTourConfig from '@/config/tour/teamForm.tour.js';
import artistListTourConfig from '@/config/tour/artistList.tour.js';
import artistFormTourConfig from '@/config/tour/artistForm.tour.js';
import playlistListTourConfig from '@/config/tour/playlistList.tour.js';
import playlistEditorTourConfig from '@/config/tour/playlistEditor.tour.js';
import songListTourConfig from '@/config/tour/songList.tour.js';
import searchModalTourConfig from '@/config/tour/searchModal.tour.js';
import profileTourConfig from '@/config/tour/profile.tour.js';

/**
 * Vue 3 Composable for mytriad Onboarding Tours.
 * Note: Tours are strictly user-initiated on demand (e.g. clicking the '?' help button).
 * Auto-start tours on screen load are intentionally prevented to respect user focus.
 */
export function useTour() {
  const { t } = useI18n();
  const activeTimers = new Set();

  if (getCurrentInstance()) {
    onUnmounted(() => {
      for (const timer of activeTimers) {
        clearTimeout(timer);
      }
      activeTimers.clear();
      if (tourService.isActive()) {
        tourService.destroy();
      }
    });
  }

  const createTourStarter = (config) => {
    let pendingTimer = null;
    return (force = false, delayMs = null) => {
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        activeTimers.delete(pendingTimer);
        pendingTimer = null;
      }

      // Tours only open when explicitly requested by user (force = true)
      if (!force) {
        return;
      }

      const delay = delayMs !== null ? delayMs : 0;

      const runStart = () => {
        if (tourService.isActive()) {
          tourService.destroy();
        }
        tourService.start(config, { t, force: true });
      };

      if (delay > 0) {
        pendingTimer = setTimeout(() => {
          activeTimers.delete(pendingTimer);
          pendingTimer = null;
          runStart();
        }, delay);
        activeTimers.add(pendingTimer);
      } else {
        runStart();
      }
    };
  };

  const startDashboardTour = createTourStarter(dashboardTourConfig);
  const resetDashboardTour = () => tourService.reset(dashboardTourConfig.storageKey);
  const isDashboardTourCompleted = () => tourService.hasSeen(dashboardTourConfig.storageKey);

  const startSongLiveTour = createTourStarter(songLiveTourConfig);
  const resetSongLiveTour = () => tourService.reset(songLiveTourConfig.storageKey);
  const isSongLiveTourCompleted = () => tourService.hasSeen(songLiveTourConfig.storageKey);

  const startSongEditorTour = createTourStarter(songEditorTourConfig);
  const resetSongEditorTour = () => tourService.reset(songEditorTourConfig.storageKey);
  const isSongEditorTourCompleted = () => tourService.hasSeen(songEditorTourConfig.storageKey);

  const startScheduleTour = createTourStarter(scheduleTourConfig);
  const resetScheduleTour = () => tourService.reset(scheduleTourConfig.storageKey);
  const isScheduleTourCompleted = () => tourService.hasSeen(scheduleTourConfig.storageKey);

  const startTeamListTour = createTourStarter(teamListTourConfig);
  const resetTeamListTour = () => tourService.reset(teamListTourConfig.storageKey);
  const isTeamListTourCompleted = () => tourService.hasSeen(teamListTourConfig.storageKey);

  const startTeamFormTour = createTourStarter(teamFormTourConfig);
  const resetTeamFormTour = () => tourService.reset(teamFormTourConfig.storageKey);
  const isTeamFormTourCompleted = () => tourService.hasSeen(teamFormTourConfig.storageKey);

  const startArtistListTour = createTourStarter(artistListTourConfig);
  const resetArtistListTour = () => tourService.reset(artistListTourConfig.storageKey);
  const isArtistListTourCompleted = () => tourService.hasSeen(artistListTourConfig.storageKey);

  const startArtistFormTour = createTourStarter(artistFormTourConfig);
  const resetArtistFormTour = () => tourService.reset(artistFormTourConfig.storageKey);
  const isArtistFormTourCompleted = () => tourService.hasSeen(artistFormTourConfig.storageKey);

  const startPlaylistListTour = createTourStarter(playlistListTourConfig);
  const resetPlaylistListTour = () => tourService.reset(playlistListTourConfig.storageKey);
  const isPlaylistListTourCompleted = () => tourService.hasSeen(playlistListTourConfig.storageKey);

  const startPlaylistEditorTour = createTourStarter(playlistEditorTourConfig);
  const resetPlaylistEditorTour = () => tourService.reset(playlistEditorTourConfig.storageKey);
  const isPlaylistEditorTourCompleted = () => tourService.hasSeen(playlistEditorTourConfig.storageKey);

  const startSongListTour = createTourStarter(songListTourConfig);
  const resetSongListTour = () => tourService.reset(songListTourConfig.storageKey);
  const isSongListTourCompleted = () => tourService.hasSeen(songListTourConfig.storageKey);

  const startSearchModalTour = createTourStarter(searchModalTourConfig);
  const resetSearchModalTour = () => tourService.reset(searchModalTourConfig.storageKey);
  const isSearchModalTourCompleted = () => tourService.hasSeen(searchModalTourConfig.storageKey);

  const startProfileTour = createTourStarter(profileTourConfig);
  const resetProfileTour = () => tourService.reset(profileTourConfig.storageKey);
  const isProfileTourCompleted = () => tourService.hasSeen(profileTourConfig.storageKey);

  return {
    startDashboardTour,
    resetDashboardTour,
    isDashboardTourCompleted,
    startSongListTour,
    resetSongListTour,
    isSongListTourCompleted,
    startSongLiveTour,
    resetSongLiveTour,
    isSongLiveTourCompleted,
    startSongEditorTour,
    resetSongEditorTour,
    isSongEditorTourCompleted,
    startScheduleTour,
    resetScheduleTour,
    isScheduleTourCompleted,
    startTeamListTour,
    resetTeamListTour,
    isTeamListTourCompleted,
    startTeamFormTour,
    resetTeamFormTour,
    isTeamFormTourCompleted,
    startArtistListTour,
    resetArtistListTour,
    isArtistListTourCompleted,
    startArtistFormTour,
    resetArtistFormTour,
    isArtistFormTourCompleted,
    startPlaylistListTour,
    resetPlaylistListTour,
    isPlaylistListTourCompleted,
    startPlaylistEditorTour,
    resetPlaylistEditorTour,
    isPlaylistEditorTourCompleted,
    startSearchModalTour,
    resetSearchModalTour,
    isSearchModalTourCompleted,
    startProfileTour,
    resetProfileTour,
    isProfileTourCompleted,
    tourService
  };
}

export default useTour;
